import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Phone, Mail, Loader2, Snowflake, Wind, Flame, HelpCircle, RefreshCcw, Trash2, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "rtp_admin_pwd";

function iconForType(t) {
  if (t === "HVAC") return Wind;
  if (t === "Refrigeration") return Snowflake;
  if (t === "Cooking Equipment") return Flame;
  return HelpCircle;
}

export default function Admin() {
  const [pwd, setPwd] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchItems = async (password) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact`, {
        headers: { "X-Admin-Password": password },
      });
      setItems(res.data || []);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, password);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Invalid admin password");
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pwd) fetchItems(pwd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!pwd.trim()) return;
    setAuthBusy(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pwd });
      await fetchItems(pwd);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) toast.error("Invalid admin password");
      else toast.error("Login failed");
    } finally {
      setAuthBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPwd("");
    setAuthed(false);
    setItems([]);
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this request? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/contact/${id}`, {
        headers: { "X-Admin-Password": pwd },
      });
      setItems((prev) => prev.filter((r) => r.id !== id));
      toast.success("Request deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const clearAll = async () => {
    if (items.length === 0) return;
    const msg = `Delete ALL ${items.length} request(s)? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    if (!window.confirm("Are you sure? This permanently wipes the contact list.")) return;
    try {
      const res = await axios.delete(`${API}/contact?confirm=YES`, {
        headers: { "X-Admin-Password": pwd },
      });
      setItems([]);
      toast.success(`Deleted ${res.data?.deleted ?? 0} request(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const downloadCsv = () => {
    if (items.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const cols = [
      "id",
      "created_at",
      "name",
      "phone",
      "email",
      "equipment_type",
      "issue_description",
      "email_sent",
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = cols.join(",");
    const rows = items.map((r) =>
      cols
        .map((c) => {
          if (c === "created_at" && r[c]) {
            try {
              return escape(new Date(r[c]).toISOString());
            } catch {
              return escape(r[c]);
            }
          }
          return escape(r[c]);
        })
        .join(",")
    );
    const csv = "\uFEFF" + [header, ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const a = document.createElement("a");
    a.href = url;
    a.download = `repairtecpro-requests-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${items.length} request(s)`);
  };

  if (!authed) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-slate-50 px-4">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white border border-slate-200 p-8"
        >
          <div className="mx-auto w-12 h-12 bg-blue-900 text-white grid place-items-center">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-display font-black text-2xl text-slate-900 mt-5 text-center">
            Admin login
          </h1>
          <p className="text-sm text-slate-500 mt-2 text-center">
            Enter the admin password to view repair requests.
          </p>
          <div className="mt-6">
            <Label htmlFor="pwd" className="text-xs uppercase tracking-wider font-semibold text-slate-700">
              Password
            </Label>
            <Input
              id="pwd"
              data-testid="admin-password-input"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoFocus
              className="mt-2 rounded-none border-slate-300 focus-visible:ring-blue-900 focus-visible:border-blue-900"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={authBusy}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-900 hover:bg-blue-800 disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold tracking-wide uppercase transition-colors"
          >
            {authBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="bg-slate-50 min-h-[80vh]">
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs tracking-[0.22em] uppercase font-semibold text-cyan-400">
              Admin
            </div>
            <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl mt-2">
              Repair requests
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchItems(pwd)}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white px-4 py-2 text-xs font-semibold tracking-wide uppercase transition-colors"
            >
              <RefreshCcw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={downloadCsv}
              disabled={items.length === 0}
              data-testid="admin-export-csv-btn"
              className="inline-flex items-center gap-2 border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400 hover:text-slate-950 hover:border-cyan-400 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-cyan-300 disabled:hover:border-cyan-400/50 px-4 py-2 text-xs font-semibold tracking-wide uppercase transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button
              onClick={clearAll}
              disabled={items.length === 0}
              data-testid="admin-clear-all-btn"
              className="inline-flex items-center gap-2 border border-red-400/50 text-red-300 hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-red-300 disabled:hover:border-red-400/50 px-4 py-2 text-xs font-semibold tracking-wide uppercase transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear all
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white px-4 py-2 text-xs font-semibold tracking-wide uppercase transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="grid place-items-center py-20 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div data-testid="admin-empty" className="bg-white border border-slate-200 p-12 text-center">
            <div className="text-slate-500">No requests yet.</div>
          </div>
        ) : (
          <div data-testid="admin-list" className="space-y-4">
            {items.map((r) => {
              const Icon = iconForType(r.equipment_type);
              const dt = r.created_at
                ? new Date(r.created_at).toLocaleString()
                : "";
              return (
                <article
                  key={r.id}
                  data-testid={`admin-request-${r.id}`}
                  className="bg-white border border-slate-200 hover:border-blue-900 transition-colors p-6 grid grid-cols-1 md:grid-cols-12 gap-6"
                >
                  <div className="md:col-span-3 flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-900 text-white grid place-items-center shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-blue-900">
                        {r.equipment_type}
                      </div>
                      <div className="font-display font-bold text-slate-900 mt-1">
                        {r.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{dt}</div>
                    </div>
                  </div>
                  <div className="md:col-span-6">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">
                      Issue
                    </div>
                    <p className="text-sm text-slate-700 mt-2 leading-relaxed whitespace-pre-wrap">
                      {r.issue_description}
                    </p>
                  </div>
                  <div className="md:col-span-3 flex flex-col gap-2">
                    <a
                      href={`tel:${(r.phone || "").replace(/\D/g, "")}`}
                      data-testid={`admin-call-${r.id}`}
                      className="inline-flex items-center justify-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-4 py-2 text-xs font-semibold tracking-wide uppercase transition-colors"
                    >
                      <Phone className="w-3.5 h-3.5" /> {r.phone}
                    </a>
                    <a
                      href={`mailto:${r.email}`}
                      data-testid={`admin-email-${r.id}`}
                      className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-900 px-4 py-2 text-xs font-semibold tracking-wide uppercase transition-colors break-all"
                    >
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{r.email}</span>
                    </a>
                    <span
                      className={`inline-flex items-center justify-center gap-1 px-3 py-1 text-[10px] tracking-[0.22em] uppercase font-bold ${
                        r.email_sent
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {r.email_sent ? "Email delivered" : "Email skipped"}
                    </span>
                    <button
                      onClick={() => deleteOne(r.id)}
                      data-testid={`admin-delete-${r.id}`}
                      className="inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 px-4 py-2 text-xs font-semibold tracking-wide uppercase transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
